import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import * as PortOne from "@portone/browser-sdk/v2";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import { updateTermsAgreement } from "../utils/terms";
// 고유 ID 생성 함수
const encodeUUID = () => {
  const randomPart = Math.random().toString(36).substring(2, 10);
  const timestampPart = Date.now().toString(36);
  return `${randomPart}${timestampPart}`;
};

const PaymentMethodsPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [selectedMethod, setSelectedMethod] = useState("");
  const [totalAmount, setTotalAmount] = useState(10000);
  const [totalApplicants, setTotalApplicants] = useState(1);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [currentApplication, setCurrentApplication] = useState(null);
  const paymentTimeoutRef = useRef(null);

  // IMP 스크립트 로드 (신용카드 결제용)
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.iamport.kr/v1/iamport.js";
    script.async = true;
    script.onload = () => {
      if (window.IMP) {
        window.IMP.init("imp60665034"); // 가맹점 식별코드
      }
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // 사용자 정보 가져오기
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
        );

        if (!token) {
          return;
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          return;
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        console.error("사용자 정보 가져오기 실패:", err);
      }
    };

    fetchUserData();
  }, []);

  // 결제 금액 계산 (서버에서 가져온 coSignerCount 사용)
  useEffect(() => {
    // userData와 applications가 모두 있어야 계산 가능
    if (
      !userData ||
      !userData.applications ||
      userData.applications.length === 0
    ) {
      return;
    }

    // 가장 최근에 생성된 STARTED 상태의 application 찾기 (결제 대기 중인 신청서)
    const startedApplications = userData.applications.filter(
      (app) => app.status === "STARTED",
    );

    let targetApplication = null;

    if (startedApplications.length > 0) {
      // STARTED 상태의 application이 있으면 가장 최근 것 선택 (createdAt 기준)
      targetApplication = startedApplications.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // 내림차순 (최신순)
      })[0];
    } else {
      // STARTED 상태가 없으면 가장 최근에 생성된 application 선택
      targetApplication = userData.applications.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // 내림차순 (최신순)
      })[0];
    }

    if (targetApplication) {
      setCurrentApplication(targetApplication);

      // coSignerCount를 사용하여 계산 (본인 1명 + 공동신청자 수)
      const coSignerCount = targetApplication.coSignerCount || 0;
      const totalApplicantsCount = 1 + coSignerCount; // 본인 1명 + 공동신청자 수
      const calculatedAmount = totalApplicantsCount * 10000;

      setTotalApplicants(totalApplicantsCount);
      setTotalAmount(calculatedAmount);
    } else {
      // application을 찾지 못한 경우 기본값으로 설정
      setTotalApplicants(1);
      setTotalAmount(10000);
      setCurrentApplication(null);
    }
  }, [userData]);

  // 페이지 포커스 시 결제 처리 중 상태 초기화 (뒤로가기로 돌아왔을 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // 페이지가 다시 보일 때 결제 처리 중 상태 초기화
        setIsPaymentProcessing(false);
        // 타임아웃도 정리
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }
      }
    };

    const handleFocus = () => {
      // 페이지가 포커스를 받을 때 결제 처리 중 상태 초기화
      setIsPaymentProcessing(false);
      // 타임아웃도 정리
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      // 컴포넌트 언마운트 시 타임아웃 정리
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
    };
  }, []);

  // 결제 방법 선택 처리
  const handleSelectPaymentMethod = (method) => {
    setSelectedMethod(method);
  };

  // 카카오페이 결제 처리
  const processKakaopayPayment = async () => {
    if (!userData) {
      alert("사용자 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    if (!currentApplication) {
      alert("신청서 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    try {
      setIsPaymentProcessing(true);
      const applicationNumber = currentApplication.applicationNumber;

      const PORTONE_STORE_ID = "store-c0cd2b2d-8f6e-4586-89d3-b545b2a73a8f";
      const KAKAOPAY_CHANNEL_KEY =
        "channel-key-5e5e130e-f83f-4da7-b115-45204e6be7a4";

      const paymentId = `pay_${encodeUUID()}`;

      // 결제 처리 중 상태를 일정 시간 후 자동으로 초기화 (타임아웃)
      paymentTimeoutRef.current = setTimeout(() => {
        setIsPaymentProcessing(false);
        paymentTimeoutRef.current = null;
      }, 300000); // 5분 후 자동 초기화

      try {
        const response = await PortOne.requestPayment({
          storeId: PORTONE_STORE_ID,
          channelKey: KAKAOPAY_CHANNEL_KEY,
          paymentId,
          orderName: "쿠팡 집단소송 접수비",
          totalAmount: totalAmount,
          // totalAmount: 10000,
          currency: "CURRENCY_KRW",
          payMethod: "EASY_PAY",
          bypass: {
            kakaopay: {
              custom_message: "하기 금액은 부가세 10%가 포함된 금액입니다.",
            },
          },
          customData: {
            applicationNumber: applicationNumber,
            price: totalAmount,
            // price: 10000,
          },
          noticeUrls: ["https://lawsuit.aitheh.com/payments/complete"],
          customer: {
            fullName: userData.name || "사용자",
            phoneNumber: userData.phoneNumber || "01012345678",
            email: userData.email || "user@example.com",
          },
          redirectUrl: `https://www.lawfirmthe-h-coupang.info/payment-verification?paymentId=${paymentId}`,
        });

        // 응답이 오면 타임아웃 제거
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }

        if (response.code != null) {
          setIsPaymentProcessing(false);
          return;
        }

        if (response.paymentId) {
          navigate(`/payment-verification?paymentId=${paymentId}`);
        }
      } catch (paymentError) {
        // 결제 창이 닫히거나 취소된 경우
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }
        console.error("결제 요청 중 오류:", paymentError);
        setIsPaymentProcessing(false);
      }
    } catch (error) {
      console.error("카카오페이 결제 처리 중 오류:", error);
      alert(
        "결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.!",
        error.message,
      );
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  // 신용카드 결제 처리
  const processCreditCardPayment = async () => {
    if (!userData) {
      alert("사용자 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    if (!window.IMP) {
      alert("결제 모듈을 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    if (!currentApplication) {
      alert("신청서 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    try {
      setIsPaymentProcessing(true);
      const applicationNumber = currentApplication.applicationNumber;
      const paymentId = `pay_${encodeUUID()}`;

      // 모바일 환경인지 확인
      const isMobile = !isDesktop;

      // KG이니시스 결제 파라미터
      const paymentParams = {
        channelKey: "channel-key-18fdaec6-3dc0-450e-90a4-96a3be932f66",
        pay_method: "card",
        merchant_uid: paymentId,
        name: "쿠팡 집단소송 접수비",
        amount: totalAmount,
        // amount: 10000,
        buyer_email: userData.email || "user@example.com",
        buyer_name: userData.name || "사용자",
        buyer_tel: userData.phoneNumber || "010-1234-5678",
        buyer_addr: userData.address || "서울특별시",
        custom_data: {
          applicationNumber: applicationNumber,
          price: totalAmount,
          // price: 10000,
        },
        notice_url: "https://lawsuit.aitheh.com/payments/complete",
      };

      // 모바일 환경에서는 리디렉션 URL 설정
      if (isMobile) {
        paymentParams.m_redirect_url =
          "https://www.lawfirmthe-h-coupang.info/payment-verification";
        paymentParams.bypass = {
          P_RESERVED: "noeasypay=Y",
        };
      } else {
        paymentParams.bypass = {
          acceptmethod: "noeasypay",
        };
      }

      // 결제 처리 중 상태를 일정 시간 후 자동으로 초기화 (타임아웃)
      paymentTimeoutRef.current = setTimeout(() => {
        setIsPaymentProcessing(false);
        paymentTimeoutRef.current = null;
      }, 300000); // 5분 후 자동 초기화

      // KG이니시스 결제창 호출
      window.IMP.request_pay(paymentParams, async (rsp) => {
        // 콜백이 호출되면 타임아웃 제거
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }

        if (rsp.success) {
          try {
            if (!isMobile) {
              navigate(`/payment-verification?imp_uid=${rsp.merchant_uid}`);
            }
          } catch (completeError) {
            console.error("결제 완료 처리 실패:", completeError);
            alert(
              "결제는 완료되었으나 처리 중 오류가 발생했습니다. 고객센터로 문의해주세요.",
            );
          }
        } else {
          console.error("결제 실패:", rsp.error_msg);
          alert(`결제에 실패했습니다: ${rsp.error_msg}`);
        }
        setIsPaymentProcessing(false);
      });
    } catch (error) {
      // 에러 발생 시 타임아웃 제거
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
      console.error("결제 처리 중 오류:", error);
      alert(
        "결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.!",
        error.message,
      );
      setIsPaymentProcessing(false);
    }
  };

  // 결제 진행 처리
  const handleContinue = async () => {
    if (!selectedMethod || !agreementChecked) {
      return;
    }
    await updateTermsAgreement(
      { hasAcknowledgedImportantNotice: true },
      currentApplication?.id,
    );
    // 선택한 결제 방법에 따라 결제 처리
    if (selectedMethod === "kakaopay") {
      processKakaopayPayment();
    } else if (selectedMethod === "creditcard") {
      processCreditCardPayment();
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>결제하기</SubTitle>
            <Description>소송 접수를 위한 비용을 결제해주세요</Description>
          </Header>

          {/* 결제 금액 섹션 */}
          <PaymentAmountSection>
            <SectionTitle>결제 금액</SectionTitle>
            <AmountRow>
              <AmountLabel>접수비</AmountLabel>
              <AmountValue>{totalAmount.toLocaleString()}원</AmountValue>
              {/* <AmountValue>10,000원</AmountValue> */}
            </AmountRow>
            <Divider />
            <CalculationInfo>
              {totalApplicants > 1 ? `10,000원 × ${totalApplicants}명` : ""}
            </CalculationInfo>
            <TotalRow>
              <TotalLabel>총 결제 금액</TotalLabel>
              <TotalValue>{totalAmount.toLocaleString()}원</TotalValue>
              {/* <TotalValue>10,000원</TotalValue> */}
            </TotalRow>
          </PaymentAmountSection>

          {/* 결제수단 섹션 */}
          <PaymentMethodSection>
            <SectionTitle>결제수단</SectionTitle>
            <PaymentMethodsContainer>
              {/* 카카오페이 */}
              <PaymentMethodCard
                isSelected={selectedMethod === "kakaopay"}
                onClick={() => handleSelectPaymentMethod("kakaopay")}
              >
                <PaymentIcon
                  src={
                    selectedMethod === "kakaopay"
                      ? "/png/kakaopayActive.png"
                      : "/png/kakaopayInactive.png"
                  }
                  alt="카카오페이"
                />
                <PaymentMethodName isSelected={selectedMethod === "kakaopay"}>
                  카카오페이
                </PaymentMethodName>
              </PaymentMethodCard>

              {/* 신용카드 */}
              <PaymentMethodCard
                isSelected={selectedMethod === "creditcard"}
                onClick={() => handleSelectPaymentMethod("creditcard")}
              >
                <PaymentIcon
                  src={
                    selectedMethod === "creditcard"
                      ? "/png/creditcardActive.png"
                      : "/png/creditCardInActive.png"
                  }
                  alt="신용카드"
                />
                <PaymentMethodName isSelected={selectedMethod === "creditcard"}>
                  신용카드
                </PaymentMethodName>
              </PaymentMethodCard>
            </PaymentMethodsContainer>
          </PaymentMethodSection>

          {/* 환불 규정 및 주의사항 */}
          <RefundPolicySection>
            <SectionTitle>환불 규정 및 주의사항</SectionTitle>
            <PolicyList>
              <PolicyItem>
                본 소송비용(₩10,000)은 소송 실비 성격의 최소 비용으로 책정되어,
                결제 이후에는 어떠한 사유로도 반환되지 않음을 확인하고
                동의합니다.
              </PolicyItem>
              <PolicyItem>
                소송비용(₩10,000)과 판결금에서 공제되는 성공보수 10%(부가세
                별도) 외의 추가 비용은 발생하지 않습니다.
              </PolicyItem>
            </PolicyList>
          </RefundPolicySection>

          {/* 동의 체크박스 */}
          <AgreementItem onClick={() => setAgreementChecked(!agreementChecked)}>
            <CheckboxImage
              src={
                agreementChecked
                  ? "/png/checked.png"
                  : "/png/checkedinActive.png"
              }
              alt="체크박스"
            />
            <AgreementText>
              상기 내용을 모두 확인하였으며 동의합니다.
            </AgreementText>
          </AgreementItem>

          {/* 결제하기 버튼 */}
          <ButtonContainer>
            <ContinueButton
              onClick={handleContinue}
              disabled={
                !selectedMethod || !agreementChecked || isPaymentProcessing
              }
            >
              {isPaymentProcessing ? "결제 처리 중..." : "결제하기"}
            </ContinueButton>
          </ButtonContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 24px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 0px;
  line-height: 1.4;
`;

const PaymentAmountSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px 0;
`;

const AmountRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const AmountLabel = styled.span`
  font-size: 16px;
  color: #666;
`;

const AmountValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin: 16px 0;
`;

const CalculationInfo = styled.div`
  font-size: 14px;
  color: #666;
  text-align: right;
  margin-bottom: 12px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #333;
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #333;
`;

const PaymentMethodSection = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 0px;
  margin-bottom: 16px;
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const PaymentMethodCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px;
  border-radius: 12px;
  background-color: white;
  border: 1px solid ${(props) => (props.isSelected ? "#3855E2" : "#e5e5e5")};
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 120px;

  &:hover {
    border-color: #3855e2;
  }
`;

const PaymentIcon = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin-bottom: 12px;
`;

const PaymentMethodName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.isSelected ? "#494949" : "#B8B8B8")};
  text-align: center;
`;

const RefundPolicySection = styled.div`
  width: 100%;
  margin-top: 16px;
  border-radius: 12px;
  padding: 0px;
  margin-bottom: 16px;
`;

const PolicyList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
`;

const PolicyItem = styled.li`
  font-size: 14px;
  color: #666;
  line-height: 1.2;
  margin-bottom: 12px;
  padding-left: 0;
  list-style: none;
  display: flex;

  &:before {
    content: "• ";
    color: #666;
    margin-right: 4px;
    flex-shrink: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const AgreementItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 24px;
  width: 100%;
`;

const CheckboxImage = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const AgreementText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: 0px;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

const ContinueButton = styled.button`
  width: 100%;
  height: 56px;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease;
`;

export default PaymentMethodsPage;
